import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  generateActionItemFromTranscript,
  transcribeActionItemAudio,
} from "../businessModeService";

const MAX_RECORDING_MS = 120000;
const INLINE_WAVEFORM_BAR_COUNT = 5;

const MicIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M12 14a3 3 0 0 0 3-3V6a3 3 0 1 0-6 0v5a3 3 0 0 0 3 3Z"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M19 11a7 7 0 0 1-14 0M12 18v3"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const StopIcon = () => (
  <svg width="10" height="10" viewBox="0 0 10 10" fill="currentColor" aria-hidden="true">
    <rect x="1" y="1" width="8" height="8" rx="1" />
  </svg>
);

/**
 * Compact level bars shown beside the mic while recording.
 *
 * @example
 * <InlineVoiceWaveform stream={mediaStream} />
 */
const InlineVoiceWaveform = ({ stream }) => {
  const [levels, setLevels] = useState(() => Array(INLINE_WAVEFORM_BAR_COUNT).fill(30));

  useEffect(() => {
    if (!stream) return undefined;

    const audioContext = new AudioContext();
    const source = audioContext.createMediaStreamSource(stream);
    const analyser = audioContext.createAnalyser();
    analyser.fftSize = 32;
    analyser.smoothingTimeConstant = 0.8;
    source.connect(analyser);

    const buffer = new Uint8Array(analyser.frequencyBinCount);
    let frameId = 0;

    const tick = () => {
      analyser.getByteFrequencyData(buffer);
      const step = Math.max(1, Math.floor(buffer.length / INLINE_WAVEFORM_BAR_COUNT));
      const next = Array.from({ length: INLINE_WAVEFORM_BAR_COUNT }, (_, index) => {
        const sample = buffer[index * step] || 0;
        return 20 + (sample / 255) * 80;
      });
      setLevels(next);
      frameId = requestAnimationFrame(tick);
    };

    frameId = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(frameId);
      source.disconnect();
      void audioContext.close();
    };
  }, [stream]);

  return (
    <span className="action-items-voice-waveform--inline" aria-hidden="true">
      {levels.map((height, index) => (
        <span
          key={index}
          className="action-items-voice-waveform--inline__bar"
          style={{ height: `${height}%` }}
        />
      ))}
    </span>
  );
};

/**
 * Microphone capture for action-item creation (record → transcribe → draft).
 * Imperative `toggle()` starts or stops recording (same as clicking the mic).
 *
 * @example
 * <VoiceActionItemCapture
 *   disabled={submitting}
 *   onDraft={({ transcript, actionItemText }) => setText(actionItemText)}
 * />
 *
 * @example
 * micRef.current.toggle();
 * // Starts recording when idle, stops when recording
 */
const VoiceActionItemCapture = React.forwardRef(function VoiceActionItemCapture(
  {
    className = "",
    disabled = false,
    onDraft,
    onPhaseChange,
    onError,
  },
  ref
) {
  const [phase, setPhase] = useState("idle");
  const [recordingStream, setRecordingStream] = useState(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const stopTimerRef = useRef(null);
  const phaseRef = useRef(phase);
  phaseRef.current = phase;

  const setPhaseSafe = useCallback(
    (next) => {
      setPhase(next);
      if (typeof onPhaseChange === "function") {
        onPhaseChange(next);
      }
    },
    [onPhaseChange]
  );

  const reportError = useCallback(
    (message) => {
      const text = String(message || "").trim();
      if (typeof onError === "function") {
        onError(text);
      }
    },
    [onError]
  );

  const cleanupStream = useCallback(() => {
    if (stopTimerRef.current) {
      clearTimeout(stopTimerRef.current);
      stopTimerRef.current = null;
    }
    const stream = streamRef.current;
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    setRecordingStream(null);
  }, []);

  useEffect(() => () => cleanupStream(), [cleanupStream]);

  const stopRecording = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== "inactive") {
      recorder.stop();
    }
    cleanupStream();
  }, [cleanupStream]);

  const processRecording = useCallback(
    async (blob) => {
      setPhaseSafe("transcribing");
      reportError("");
      try {
        const { transcript } = await transcribeActionItemAudio(blob);
        setPhaseSafe("generating");
        const draft = await generateActionItemFromTranscript(transcript);
        onDraft({
          transcript,
          actionTitle: draft.action_title,
          actionItemText: draft.action_item_text,
        });
        setPhaseSafe("idle");
      } catch (err) {
        setPhaseSafe("idle");
        reportError(String(err?.message || "Voice capture failed"));
      }
    },
    [onDraft, reportError, setPhaseSafe]
  );

  const startRecording = useCallback(async () => {
    if (disabled || phaseRef.current !== "idle") return;
    reportError("");
    chunksRef.current = [];

    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (err) {
      reportError(String(err?.message || "Microphone permission denied"));
      return;
    }

    streamRef.current = stream;
    setRecordingStream(stream);

    const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
      ? "audio/webm;codecs=opus"
      : "audio/webm";
    const recorder = new MediaRecorder(stream, { mimeType });
    mediaRecorderRef.current = recorder;

    recorder.ondataavailable = (event) => {
      if (event.data && event.data.size > 0) {
        chunksRef.current.push(event.data);
      }
    };

    recorder.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: mimeType.split(";")[0] });
      chunksRef.current = [];
      if (!blob.size) {
        setPhaseSafe("idle");
        reportError("Recording was empty");
        return;
      }
      void processRecording(blob);
    };

    recorder.start();
    setPhaseSafe("recording");
    stopTimerRef.current = setTimeout(() => {
      stopRecording();
    }, MAX_RECORDING_MS);
  }, [disabled, processRecording, reportError, setPhaseSafe, stopRecording]);

  const handleMicClick = useCallback(() => {
    if (phaseRef.current === "recording") {
      stopRecording();
      return;
    }
    if (phaseRef.current === "idle") {
      void startRecording();
    }
  }, [startRecording, stopRecording]);

  React.useImperativeHandle(
    ref,
    () => ({
      toggle: handleMicClick,
      getPhase: () => phaseRef.current,
    }),
    [handleMicClick]
  );

  const isBusy = phase === "transcribing" || phase === "generating";
  const isRecording = phase === "recording";

  const ariaLabel = isRecording
    ? "Stop recording"
    : isBusy
      ? "Processing voice recording"
      : "Dictate task";

  const buttonClass = [
    "action-items-add-form__mic-btn",
    isRecording ? "action-items-add-form__mic-btn--stop" : "",
    isBusy ? "action-items-add-form__mic-btn--busy" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const clusterClass = [
    "action-items-add-form__mic-cluster",
    isRecording ? "action-items-add-form__mic-cluster--recording" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={clusterClass}>
      <button
        type="button"
        className={buttonClass}
        onClick={handleMicClick}
        disabled={disabled || isBusy}
        aria-label={ariaLabel}
        aria-pressed={isRecording}
        data-tip={ariaLabel}
      >
        {isBusy ? (
          <span className="action-items-add-form__mic-spinner" aria-hidden="true" />
        ) : isRecording ? (
          <StopIcon />
        ) : (
          <MicIcon />
        )}
      </button>
      {isRecording && recordingStream ? (
        <InlineVoiceWaveform stream={recordingStream} />
      ) : null}
    </div>
  );
});

export default VoiceActionItemCapture;
